import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/types/linguadeck";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

type TermDelimiterType = "tab" | "comma" | "custom";
type RowDelimiterType = "newline" | "semicolon" | "custom";

const TERM_DELIMITER_VALUES: Record<TermDelimiterType, string> = {
  tab: "\t",
  comma: ",",
  custom: "",
};

const ROW_DELIMITER_VALUES: Record<RowDelimiterType, string> = {
  newline: "\n",
  semicolon: ";",
  custom: "",
};

interface ImportModalProps {
  open: boolean;
  onClose: () => void;
  onImport: (cards: Card[]) => void;
}

export default function ImportModal({ open, onClose, onImport }: ImportModalProps) {
  const { t } = useTranslation();
  const [rawText, setRawText] = useState("");
  const [termDelimiter, setTermDelimiter] = useState<TermDelimiterType>("tab");
  const [termCustom, setTermCustom] = useState("");
  const [rowDelimiter, setRowDelimiter] = useState<RowDelimiterType>("newline");
  const [rowCustom, setRowCustom] = useState("");

  const termSep = termDelimiter === "custom" ? termCustom : TERM_DELIMITER_VALUES[termDelimiter];
  const rowSep =
    rowDelimiter === "custom"
      ? rowCustom
      : ROW_DELIMITER_VALUES[rowDelimiter];

  const parsedCards = useMemo(() => {
    const trimmed = rawText.trim();
    if (!trimmed) return [];
    const sep = rowSep || "\n";
    const rows = trimmed.split(sep).map((r) => r.trim()).filter(Boolean);
    const cards: Card[] = [];
    const isTab = termDelimiter === "tab";
    const tSep = termSep || "\t";
    for (const row of rows) {
      const parts = isTab
        ? row.split(/\t+|\s{2,}/).map((p) => p.trim())
        : row.split(tSep).map((p) => p.trim());
      const front = (parts[0] ?? "").trim();
      const back = (parts[1] ?? "").trim();
      if (front || back) {
        cards.push({ front, back });
      }
    }
    return cards;
  }, [rawText, termSep, rowSep, termDelimiter]);

  const handleImport = () => {
    onImport(parsedCards);
    setRawText("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0"
      >
        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-2">
          <DialogTitle>{t("deckModal.import")}</DialogTitle>
        </DialogHeader>

        <div
          className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-5 p-4 px-6"
          style={{ fontFamily: "DM Sans, sans-serif" }}
        >
          <div>
            <label
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t("importModal.pasteData")}
            </label>
            <textarea
              rows={6}
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              placeholder={t('importModal.examplePlaceholder')}
              className="w-full min-h-0 max-h-[180px] resize-y px-4 py-3 rounded-xl border border-gray-200 dark:border-[hsl(var(--border))] bg-gray-50 dark:bg-[hsl(var(--bg3))] text-gray-800 dark:text-[hsl(var(--foreground))] placeholder-gray-400 dark:placeholder-gray-500 focus:bg-white dark:focus:bg-[hsl(var(--bg2))] focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                {t("importModal.termDelimiter")}
              </label>
              <div className="flex flex-wrap gap-4">
                {(["tab", "comma", "custom"] as const).map((opt) => (
                  <label key={opt} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="termDelimiter"
                      checked={termDelimiter === opt}
                      onChange={() => setTermDelimiter(opt)}
                      className="text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {opt === "tab" && "Tab"}
                      {opt === "comma" && t("importModal.comma")}
                      {opt === "custom" && t("importModal.custom")}
                    </span>
                  </label>
                ))}
              </div>
              {termDelimiter === "custom" && (
                <input
                  type="text"
                  value={termCustom}
                  onChange={(e) => setTermCustom(e.target.value)}
                  placeholder={t("importModal.exampleDelimiter")}
                  className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--bg3))] text-sm"
                />
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                {t("importModal.rowDelimiter")}
              </label>
              <div className="flex flex-wrap gap-4">
                {(["newline", "semicolon", "custom"] as const).map((opt) => (
                  <label key={opt} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="rowDelimiter"
                      checked={rowDelimiter === opt}
                      onChange={() => setRowDelimiter(opt)}
                      className="text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {opt === "newline" && t("importModal.newline")}
                      {opt === "semicolon" && t("importModal.semicolon")}
                      {opt === "custom" && t("importModal.custom")}
                    </span>
                  </label>
                ))}
              </div>
              {rowDelimiter === "custom" && (
                <input
                  type="text"
                  value={rowCustom}
                  onChange={(e) => setRowCustom(e.target.value)}
                  placeholder={t("importModal.exampleRowDelimiter")}
                  className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--bg3))] text-sm"
                />
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              {t("importModal.preview")}
            </label>
            <div
              className="rounded-xl border border-gray-200 dark:border-[hsl(var(--border))] bg-gray-50 dark:bg-[hsl(var(--bg3))] p-4 min-h-[80px] max-h-[160px] overflow-y-auto"
            >
              {parsedCards.length === 0 ? (
                <span className="text-sm text-gray-400 dark:text-gray-500">
                  {t("importModal.previewEmpty")}
                </span>
              ) : (
                <div className="space-y-2">
                  {parsedCards.slice(0, 5).map((c, i) => (
                    <div
                      key={i}
                      className="flex gap-3 text-sm"
                    >
                      <span className="font-medium text-gray-800 dark:text-[hsl(var(--foreground))]">
                        {c.front || "—"}
                      </span>
                      <span className="text-gray-500">→</span>
                      <span className="text-gray-600 dark:text-gray-400">
                        {c.back || "—"}
                      </span>
                    </div>
                  ))}
                  {parsedCards.length > 5 && (
                    <span className="text-xs text-gray-400">
                      {t("importModal.andMore", {
                        count: parsedCards.length - 5,
                      })}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 p-4 border-t border-gray-200 dark:border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-gray-200 dark:border-[hsl(var(--border))] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[hsl(var(--bg3))] text-sm font-medium transition-colors"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={parsedCards.length === 0}
            className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
          >
            {t("deckModal.import")} ({parsedCards.length})
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
